import { useCallback, useEffect, useRef, useState } from 'react';
import { batchUploadAudiosToOss, batchUploadImagesToOss, batchUploadVideosToOss } from '../../api/agiBackUpload';
import {
  createRealPersonPortrait,
  deleteRealPersonPortrait,
  listRealPersonPortraits,
} from '../../api/realPerson';
import type { Asset } from '../../store/seedanceTypes';
import {
  LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  VP_ASSET_TYPE_AUDIO,
  VP_ASSET_TYPE_IMAGE,
  VP_ASSET_TYPE_VIDEO,
  VIRTUAL_PORTRAIT_PROJECT,
} from '../../utils/seedanceConstants';
import { fetchRealPersonBoundGroupIds } from '../../utils/realPortraitAuth';
import {
  computeLibraryPortraitInitialPageSize,
  computeLibraryPortraitLoadMorePageSize,
} from '../../utils/libraryPortraitPaging';
import { mapVirtualPortraitApiListToAssets } from '../../utils/seedancePortraitUtils';
import { enrichPortraitAssetsMissingUrls } from '../../utils/enrichPortraitAssetUrls';
import {
  isLibraryAudioFile,
  validateLibraryAudioFiles,
} from '../../utils/libraryAudioUpload';
import {
  computeAdaptiveBackfillPageSize,
  LIBRARY_SPARSE_BACKFILL_MAX_PAGES,
  shouldContinueSparseBackfill,
} from '../../utils/libraryPortraitSparseLoad';
import { isAbortLike } from '../../utils/seedanceRequestUtils';
import {
  getLocalStorageValue,
  removeLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
  setLocalStorageValue,
} from '../../utils/seedanceStorage';

interface UseRealPersonLibraryArgs {
  active: boolean;
  hasTaskListConfig: () => boolean;
  openConfig: () => void;
  showToast: (msg: string, type?: 's' | 'e' | 'i') => void;
  addDebug: (msg: string, isError?: boolean) => void;
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  loadMoreSentinelRef: React.RefObject<HTMLDivElement | null>;
}

const realPersonPagingSession = {
  fetchSeq: 0,
  page: 0,
  pageSize: LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  loadedGroupId: '',
};

export function useRealPersonLibrary({
  active,
  hasTaskListConfig,
  openConfig,
  showToast,
  addDebug,
  scrollRootRef,
  loadMoreSentinelRef,
}: UseRealPersonLibraryArgs) {
  const [boundGroupIds, setBoundGroupIds] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupIdState] = useState(() =>
    getLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitSelectedGroupId).trim(),
  );
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const apiOptions = useCallback(
    () => ({ onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) }),
    [addDebug],
  );

  const measurePageSize = useCallback(
    (mode: 'initial' | 'more') => {
      const root = scrollRootRef.current;
      const toolbar = root?.querySelector('.lib-real-toolbar-row');
      const toolbarH = toolbar?.getBoundingClientRect().height ?? 120;
      const pad =
        typeof window !== 'undefined'
          ? parseFloat(getComputedStyle(root ?? document.documentElement).paddingTop || '0') +
            parseFloat(getComputedStyle(root ?? document.documentElement).paddingBottom || '0')
          : 32;
      const containerWidth =
        root?.clientWidth ??
        (typeof window !== 'undefined' ? Math.min(window.innerWidth - 280, 1200) : 960);
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
      if (mode === 'initial') {
        return computeLibraryPortraitInitialPageSize({
          viewportHeight,
          containerWidth,
          reservedTopPx: toolbarH + pad + 24,
        });
      }
      return computeLibraryPortraitLoadMorePageSize(containerWidth);
    },
    [scrollRootRef],
  );

  const applySelectedGroupId = useCallback((groupId: string) => {
    const id = groupId.trim();
    setSelectedGroupIdState(id);
    if (id) {
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitSelectedGroupId, id);
    } else {
      removeLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitSelectedGroupId);
    }
    realPersonPagingSession.loadedGroupId = '';
  }, []);

  const refreshBoundGroups = useCallback(
    async (preferredGroupId?: string) => {
      if (!hasTaskListConfig()) {
        setBoundGroupIds([]);
        applySelectedGroupId('');
        setAssets([]);
        setHasMore(false);
        return [];
      }
      setGroupsLoading(true);
      try {
        const ids = await fetchRealPersonBoundGroupIds(apiOptions());
        setBoundGroupIds(ids);
        const preferred = preferredGroupId?.trim();
        setSelectedGroupIdState((cur) => {
          const next =
            preferred && ids.includes(preferred)
              ? preferred
              : cur && ids.includes(cur)
                ? cur
                : ids[0] || '';
          if (next) {
            setLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitSelectedGroupId, next);
          } else {
            removeLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitSelectedGroupId);
          }
          if (next !== cur) {
            realPersonPagingSession.loadedGroupId = '';
          }
          return next;
        });
        return ids;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`查询已绑定真人素材组失败: ${msg}`, true);
        showToast('获取真人素材组失败: ' + msg, 'e');
        setBoundGroupIds([]);
        return [];
      } finally {
        setGroupsLoading(false);
      }
    },
    [addDebug, apiOptions, applySelectedGroupId, hasTaskListConfig, showToast],
  );

  const fetchPortraitPage = useCallback(
    async (
      groupId: string,
      page: number,
      pageSize: number,
      seq: number,
      mode: 'replace' | 'append',
      signal?: AbortSignal,
    ) => {
      const data = await listRealPersonPortraits(
        groupId,
        { ...apiOptions(), signal },
        { page_number: page, page_size: pageSize },
      );
      if (seq !== realPersonPagingSession.fetchSeq) return null;
      let mapped = mapVirtualPortraitApiListToAssets(data);
      mapped = await enrichPortraitAssetsMissingUrls(
        mapped,
        groupId,
        VIRTUAL_PORTRAIT_PROJECT,
        { ...apiOptions(), signal },
      );
      if (seq !== realPersonPagingSession.fetchSeq) return null;
      if (mode === 'replace') {
        setAssets(mapped);
      } else {
        setAssets((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          return [...prev, ...mapped.filter((a) => !seen.has(a.id))];
        });
      }
      realPersonPagingSession.page = page;
      if (mode === 'replace') {
        realPersonPagingSession.loadedGroupId = groupId;
      }
      setHasMore(mapped.length >= pageSize);
      return mapped;
    },
    [apiOptions],
  );

  const loadAssetsInitial = useCallback(
    async (groupId: string, seq: number, signal?: AbortSignal) => {
      if (seq !== realPersonPagingSession.fetchSeq) return;
      const basePageSize = measurePageSize('initial');
      realPersonPagingSession.pageSize = basePageSize;
      realPersonPagingSession.page = 0;
      setAssets([]);
      setLoading(true);
      setHasMore(false);
      try {
        addDebug(`真人人像库：首屏加载 group=${groupId} page=1 page_size=${basePageSize}`);
        let batch = await fetchPortraitPage(groupId, 1, basePageSize, seq, 'replace', signal);
        if (!batch || seq !== realPersonPagingSession.fetchSeq) return;

        let batchCount = batch.length;
        let batchPageSize = basePageSize;
        let hasMoreLocal = batchCount >= batchPageSize;
        let backfillAttempt = 0;
        let loadedCount = batchCount;

        while (
          hasMoreLocal &&
          backfillAttempt < LIBRARY_SPARSE_BACKFILL_MAX_PAGES &&
          shouldContinueSparseBackfill({
            filter: 'all',
            loadedCount,
            lastPageCount: batchCount,
            lastPageSize: batchPageSize,
            hasMore: hasMoreLocal,
            minFillCount: basePageSize,
          })
        ) {
          if (seq !== realPersonPagingSession.fetchSeq) return;
          const nextPageSize = computeAdaptiveBackfillPageSize(basePageSize, backfillAttempt + 1);
          const nextPage = realPersonPagingSession.page + 1;
          backfillAttempt += 1;
          addDebug(
            `真人人像库：稀疏补拉 page=${nextPage} page_size=${nextPageSize}（合并加载，减少请求次数）`,
          );
          realPersonPagingSession.pageSize = nextPageSize;
          batch = await fetchPortraitPage(groupId, nextPage, nextPageSize, seq, 'append', signal);
          if (!batch || seq !== realPersonPagingSession.fetchSeq) return;
          batchCount = batch.length;
          batchPageSize = nextPageSize;
          hasMoreLocal = batchCount >= batchPageSize;
          loadedCount += batchCount;
        }
      } catch (e: unknown) {
        if (isAbortLike(e)) return;
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`获取真人素材列表失败: ${msg}`, true);
        showToast('获取真人素材列表失败: ' + msg, 'e');
      } finally {
        setLoading(false);
      }
    },
    [addDebug, fetchPortraitPage, measurePageSize, showToast],
  );

  const loadMoreAssets = useCallback(
    async (groupId: string, seq: number) => {
      if (seq !== realPersonPagingSession.fetchSeq) return;
      if (loading || loadingMore || !hasMore) return;
      const pageSize = measurePageSize('more');
      const nextPage = realPersonPagingSession.page + 1;
      setLoadingMore(true);
      try {
        addDebug(`真人人像库：下拉加载 page=${nextPage} page_size=${pageSize}`);
        await fetchPortraitPage(groupId, nextPage, pageSize, seq, 'append');
      } catch (e: unknown) {
        if (isAbortLike(e)) return;
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`加载更多真人素材失败: ${msg}`, true);
        showToast('加载更多失败: ' + msg, 'e');
      } finally {
        setLoadingMore(false);
      }
    },
    [addDebug, fetchPortraitPage, hasMore, loading, loadingMore, measurePageSize, showToast],
  );

  const reloadAssets = useCallback(
    (overrideGroupId?: string) => {
      const gid = (overrideGroupId ?? selectedGroupId).trim();
      if (!gid) return;
      realPersonPagingSession.loadedGroupId = '';
      realPersonPagingSession.fetchSeq += 1;
      void loadAssetsInitial(gid, realPersonPagingSession.fetchSeq);
    },
    [loadAssetsInitial, selectedGroupId],
  );

  const handleRealGroupChange = useCallback(
    (nextGroupId: string) => {
      const id = nextGroupId.trim();
      applySelectedGroupId(id);
      if (id) {
        reloadAssets(id);
      }
    },
    [applySelectedGroupId, reloadAssets],
  );

  const uploadToSelectedGroup = useCallback(
    async (input: HTMLInputElement | { files: FileList }) => {
      const gid = selectedGroupId.trim();
      const allFiles = Array.from(input.files || []);
      if ('value' in input) input.value = '';

      if (!allFiles.length) return;
      if (!gid) {
        showToast('请先选择真人素材组', 'e');
        return;
      }
      if (!hasTaskListConfig()) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        openConfig();
        return;
      }

      const imageFiles = allFiles.filter((f) => f.type.startsWith('image/'));
      const videoFiles = allFiles.filter((f) => f.type.startsWith('video/'));
      const audioCandidates = allFiles.filter(isLibraryAudioFile);
      const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
      const validVideoFiles = videoFiles.filter((f) => f.size <= MAX_VIDEO_SIZE);
      const oversizedVideos = videoFiles.filter((f) => f.size > MAX_VIDEO_SIZE);
      if (oversizedVideos.length > 0) {
        showToast(
          `以下视频超过了 50MB 限制，已跳过：${oversizedVideos.map((v) => v.name).join(', ')}`,
          'i',
        );
      }

      const audioValidation = await validateLibraryAudioFiles(audioCandidates);
      if (audioValidation.invalidFormat.length > 0) {
        showToast(
          `仅支持 mp3、wav 格式，已跳过：${audioValidation.invalidFormat.map((f) => f.name).join(', ')}`,
          'i',
        );
      }
      if (audioValidation.oversized.length > 0) {
        showToast(
          `以下音频超过了 15MB 限制，已跳过：${audioValidation.oversized.map((f) => f.name).join(', ')}`,
          'i',
        );
      }
      if (audioValidation.invalidDuration.length > 0) {
        showToast(
          `以下音频时长不在 2–15 秒范围内，已跳过：${audioValidation.invalidDuration.map(({ file }) => file.name).join(', ')}`,
          'i',
        );
      }

      const validAudioFiles = audioValidation.valid;
      const totalToUpload = imageFiles.length + validVideoFiles.length + validAudioFiles.length;
      if (totalToUpload === 0) return;

      setUploading(true);
      try {
        if (imageFiles.length > 0) {
          const imageUrls = await batchUploadImagesToOss(imageFiles, apiOptions());
          for (let i = 0; i < imageFiles.length; i++) {
            const file = imageFiles[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createRealPersonPortrait(
              { group_id: gid, url: imageUrls[i], name, asset_type: VP_ASSET_TYPE_IMAGE },
              apiOptions(),
            );
          }
        }
        if (validVideoFiles.length > 0) {
          const videoUrls = await batchUploadVideosToOss(validVideoFiles, apiOptions());
          for (let i = 0; i < validVideoFiles.length; i++) {
            const file = validVideoFiles[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createRealPersonPortrait(
              { group_id: gid, url: videoUrls[i], name, asset_type: VP_ASSET_TYPE_VIDEO },
              apiOptions(),
            );
          }
        }
        if (validAudioFiles.length > 0) {
          const audioUrls = await batchUploadAudiosToOss(validAudioFiles, apiOptions());
          for (let i = 0; i < validAudioFiles.length; i++) {
            const file = validAudioFiles[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createRealPersonPortrait(
              { group_id: gid, url: audioUrls[i], name, asset_type: VP_ASSET_TYPE_AUDIO },
              apiOptions(),
            );
          }
        }
        showToast(`已上传 ${totalToUpload} 个素材`, 's');
        reloadAssets();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`上传真人素材失败: ${msg}`, true);
        showToast('上传失败: ' + msg, 'e');
      } finally {
        setUploading(false);
      }
    },
    [
      addDebug,
      apiOptions,
      hasTaskListConfig,
      openConfig,
      reloadAssets,
      selectedGroupId,
      showToast,
    ],
  );

  const deleteAsset = useCallback(
    async (asset: Asset) => {
      const gid = selectedGroupId.trim();
      const assetId = asset.sourceAssetId?.trim() || asset.id.trim();
      if (!gid || !assetId) return;
      setDeletingAssetId(asset.id);
      try {
        await deleteRealPersonPortrait(assetId, gid, apiOptions());
        showToast('已删除素材', 's');
        reloadAssets();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`删除真人素材失败: ${msg}`, true);
        showToast('删除失败: ' + msg, 'e');
      } finally {
        setDeletingAssetId(null);
      }
    },
    [addDebug, apiOptions, reloadAssets, selectedGroupId, showToast],
  );

  useEffect(() => {
    if (!active) {
      realPersonPagingSession.fetchSeq += 1;
      return;
    }
    void refreshBoundGroups();
  }, [active, refreshBoundGroups]);

  useEffect(() => {
    if (!active || !selectedGroupId) {
      realPersonPagingSession.fetchSeq += 1;
      realPersonPagingSession.loadedGroupId = '';
      setAssets([]);
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    if (realPersonPagingSession.loadedGroupId === selectedGroupId) return;
    realPersonPagingSession.fetchSeq += 1;
    const seq = realPersonPagingSession.fetchSeq;
    void loadAssetsInitial(selectedGroupId, seq);
  }, [active, loadAssetsInitial, selectedGroupId]);

  useEffect(() => {
    if (!active || !selectedGroupId || !hasMore) return;
    const root = scrollRootRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMoreAssets(selectedGroupId, realPersonPagingSession.fetchSeq);
        }
      },
      { root, rootMargin: '240px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [
    active,
    assets.length,
    hasMore,
    loadMoreAssets,
    loadMoreSentinelRef,
    loading,
    loadingMore,
    scrollRootRef,
    selectedGroupId,
  ]);

  return {
    boundGroupIds,
    groupsLoading,
    selectedGroupId,
    setSelectedGroupId: applySelectedGroupId,
    assets,
    loading,
    loadingMore,
    hasMore,
    uploading,
    deletingAssetId,
    uploadInputRef,
    refreshBoundGroups,
    reloadAssets,
    handleRealGroupChange,
    uploadToSelectedGroup,
    deleteAsset,
  };
}
